<template>
	<b-overlay no-center :show="isOverlayDisplayed" class="d-flex flex-column h-100 w-100">
		<!-- La balise KeepAlive permet de garder le component chargé en cache pour éviter les rechargements intempestifs -->
		<!-- <KeepAlive :max="10"> -->
			<!-- Affichage de la fonction sélectionnée dans le menu (cf Features.vue) -->
			<component v-if="featureTarget !== '' && !isOverlayDisplayed" v-bind:is="featureTarget" ref="featureComponent" :Variant="Variant" :key="refreshKey" @HideOverlayEvent="hideOverlay()" />
		<!-- </KeepAlive> -->

		<template #overlay>
			<!-- Affichage d'un message si aucun noeud n'a été sélectionné -->
			<Welcome v-if="displayedOverlay === overlayEnum.SingleNodeSelection" :DisplayBrand="false" :Variant="Variant">
				<!-- Suppression message de bienvenue par défaut-->
                <template v-slot:MiscContent>
					<!-- PleaseSelectAnObjectInTree -> Veuillez sélectionner un objet dans l'arborescence-->
					<h4 class="text-center">{{ $t('PleaseSelectAnObjectInTree') }}</h4>
                </template>
			</Welcome>

			<Welcome v-else-if="featureTarget === ''" :Variant="Variant" />
		</template>
	</b-overlay>
</template>

<script lang="ts">
	import Component, { mixins } from 'vue-class-component';
	import { Inject, Prop, Watch } from 'vue-property-decorator';
    import { Getter } from 'vuex-class';
	import Welcome from '@/components/commons/Welcome.vue';
	import ObjectDisplaying from "./FeatureContainer/ObjectDisplaying.vue";
	import IFrameLoaderFiltered from "./FeatureContainer/IFrameLoaderFiltered.vue";
    import Analysis from './FeatureContainer/Analysis.vue';
	import Exploitation from './FeatureContainer/Reporting/Exploitation.vue';
    import ServiceOrder from './FeatureContainer/ServiceOrder.vue';
	import OverlayEnum from '@/enums/OverlayEnum';
	import SelectModeEnum from '@/enums/SelectModeEnum';
	import { OverlayUtils } from '@/components/commons/Utils';
	import SaveModeEnum from '@/enums/SaveModeEnum';
	import TreeNode from '@/types/TreeNode';
	import { MAIN_FILTER_TREE } from '@/App.settings';
	import { IFeatureManager, NavigationMediator } from '@/mediators/NavigationMediator';
	import DistributorDatas from './FeatureContainer/DistributorDatas.vue';
	import IFeature from '@/interfaces/IFeature';
	import ReportingStatements from './FeatureContainer/Reporting/Statements.vue';

	/** Composant mode Affichage affichant les fonctions sélectionnées */
	@Component({
		components: {
			/** Page de bienvenu quand aucune fonction n'est sélectionnée */
			Welcome,
			/** Composant de fiche objet */
			ObjectDisplaying,
			/** Composant spécifique pour affichage IFrame de fonctions V2 */
			IFrameLoaderFiltered,
			/** Composant spécifique pour la gestion d'analyse */
            Analysis,
            /** Composant spécifique pour la gestion des ordres de service du groupement d'achat */
            ServiceOrder,
			/** Composant Générateur de rapport */
			Exploitation,
			/** Composant d'exportation des données distributeurs */
			DistributorDatas,
			/** Composant d'exportion des relevés */
            ReportingStatements,
		}
	})
	export default class FeatureContainer extends mixins(OverlayUtils) implements IFeatureManager {
		/** Pour permettre l'utilisation des enums et objet screen dans la section template */
		private overlayEnum = OverlayEnum;

		/** Clé utilisée pour forcer le rafraichissement de la balise <component> pour rechargement de la fonction en cours d'utilisation */
		private refreshKey: number = 0;

		/** Composant cible de la Feature à charger */
        @Getter('GetFeatureTarget')
        private featureTarget!: string;

		/** Variant de couleur à appliquer sur le composant */
		@Prop({ default: () => "secondary" })
		public Variant!: string;

		@Getter("ObjectStore/GetSaveMode")
		private saveMode!: SaveModeEnum;

        private singleNode!: TreeNode;

		/**
		 * Applique dans le composant le noeud passé en paramètre
		 * @param node TreeNode
		 * */
        public SetNode(node: TreeNode): Promise<any> {
            return new Promise(async (resolve, reject) => {
                this.singleNode = node;
				this.nodeUpdated();
                resolve('SingleNode affecté !');
            });
        }

		/** Traitements à réaliser lors de la création d'un nouvel objet */
		public OnNewObject() {
			console.debug("FeatureContainer - OnNewObject()");
			this.hideOverlay();
		}

		/** Traitements à réaliser lors de l'Annulation de la création d'un nouvel objet */
		public OnCancelNew(){
			console.debug("FeatureContainer - OnCancelNew()");
			if (this.singleNode == undefined || (this.singleNode != undefined && (Object.keys(this.singleNode).length == 0 || this.singleNode.ObjectType == "Client")))
				this.displayOverlay(OverlayEnum.SingleNodeSelection, 1);
			else
				this.nodeUpdated();
		}

		/** Méthode pour recharger le composant de la feature en cours */
		public ReloadFeature() {
			++this.refreshKey;
		}

		/** Mode de sélection de la fonction en cours */
		@Getter(MAIN_FILTER_TREE + '/GetSelectMode')
		protected selectMode!: SelectModeEnum;

		private nodeUpdated() {
			/** S'il n'y a pas de noeud sélectionné ou qu'il s'agit d'un noeud client */
			if (this.selectMode != undefined && this.selectMode == SelectModeEnum.Single
				&& this.saveMode != SaveModeEnum.NewObject
				&& (this.singleNode == undefined || (this.singleNode != undefined && (Object.keys(this.singleNode).length == 0 || this.singleNode.ObjectType == "Client"))) ) {
				// Affiche un masque au-dessus du formulaire tant qu'un id de noeud n'est pas sélectionné
				this.displayOverlay(OverlayEnum.SingleNodeSelection, 1);
			}
			else {
				this.hideOverlay();

				/** Si le composant de la feature implémente l'interface INodeSelector alors le charge dans le composant
				 * @description C'est fait ici au lieu de le faire par le navigatorMediator car le composant n'est pas chargé lors du premier click et ne peut donc faire de SetSingleNode dans le composant
				 */
				this.$nextTick(() => {
					if ('featureComponent' in this.$refs && this.$refs['featureComponent'] && 'SetNode' in this.$refs['featureComponent'] && typeof this.$refs['featureComponent'].SetNode === 'function') {
						console.debug('FeatureContainer - nodeUpdated() - SetNode()');
						(this.$refs['featureComponent'] as IFeature).SetNode(this.singleNode);
					}
				})
			}
		}


		@Inject()
		private navigationMediator: NavigationMediator;

		mounted() {
			this.navigationMediator.RegisterFeatureManager(this);
			/** On affiche l'overlay de sélection au démarrage car pas de noeud par défaut */
			this.displayOverlay(OverlayEnum.SingleNodeSelection, 1);
		}

		beforeDestroy() {
			this.navigationMediator.Unregister(this);
		}
	}
</script>

<style scoped>
</style>